/**
 * The audit event taxonomy.
 *
 * Every event type an auditor may be asked about is enumerated here with the
 * SOX control it evidences. The ledger writer will not accept an event type
 * outside this list, so a new kind of privileged action cannot be shipped
 * without also declaring how it is evidenced.
 */

export const AUDIT_EVENT_TYPES = {
  // -------------------------------------------------------- authentication
  'auth.signin.success': 'User completed Entra ID sign-in and a session was established',
  'auth.signin.denied': 'Sign-in rejected: user disabled, tenant mismatch or no entitlement',
  'auth.signout': 'User signed out',
  'auth.token.rejected': 'Bearer token failed validation',
  'auth.session.expired': 'Session expired and was terminated',

  // --------------------------------------------------------- authorization
  'authz.decision': 'An authorization decision was evaluated for a subject, action and resource',
  'authz.denied': 'An action was refused because the subject lacked an effective permission',

  // ------------------------------------------------------------ identities
  'user.created': 'User record created on first successful sign-in',
  'user.updated': 'User profile attributes changed',
  'user.disabled': 'User disabled and all sessions revoked',
  'user.enabled': 'User re-enabled',
  'user.role.assigned': 'A role was assigned to a user',
  'user.role.revoked': 'A role was revoked from a user',
  /**
   * Recorded because it changes how this user reads every timestamp in the
   * tool, including the audit trail itself. An auditor asking why two people
   * describe the same event as happening at different times needs to be able
   * to see that one of them changed their display zone.
   */
  'user.preference.updated': 'A user changed a display preference',

  // ---------------------------------------------------------------- roles
  'role.created': 'Role created',
  'role.updated': 'Role renamed or description changed',
  'role.deleted': 'Role deleted',
  'role.permission.granted': 'A permission was added to a role',
  'role.permission.revoked': 'A permission was removed from a role',
  'role.target.granted': 'A role was scoped to a connection target',
  'role.target.revoked': 'A role scope over a connection target was removed',
  'role.sod.override': 'A toxic permission combination was accepted with documented justification',
  'role.group.mapped': 'An Entra group was mapped to a role',
  'role.group.unmapped': 'An Entra group mapping was removed',
  'role.group.sync': 'Entra group membership synchronisation ran',

  // ------------------------------------------------------------- targets
  'target.created': 'Connection target registered',
  'target.updated': 'Connection target configuration changed',
  'target.disabled': 'Connection target disabled',
  'target.enabled': 'Connection target re-enabled',
  'target.secret.bound': 'A Secrets Manager secret was bound to a connection target',
  'target.secret.unbound': 'A secret binding was removed from a connection target',
  /**
   * A configuration change that loosened a control on a target -- a widened
   * path or egress allowlist, a raised cap, certificate validation turned
   * off. Its own type, not a flag on `target.updated`, because alerting
   * keys on the type: a new egress range buried in the detail of an
   * ordinary update surfaces only if somebody happens to read it.
   */
  'target.weakened': 'A target configuration change loosened a control',
  'target.tested': 'A connectivity test was run against a target',
  'target.proc.allowlisted': 'A stored procedure was added to a target allowlist',
  'target.proc.removed': 'A stored procedure was removed from a target allowlist',
  'target.circuit.opened': 'The circuit breaker for a target opened after repeated failures',
  'target.circuit.closed': 'The circuit breaker for a target closed',
  /**
   * Distinct from `target.circuit.closed`, which the tool decides for
   * itself after a successful probe. This one has a person behind it, and
   * it re-admits traffic to a target the tool had judged unwell -- so it
   * needs an actor against it, not just a state change.
   */
  'target.circuit.reset': 'An administrator forced the circuit breaker for a target closed',

  // ----------------------------------------------------------- submissions
  'request.submitted': 'A user submitted a query or API request for execution',
  'request.classified': 'The policy engine classified a submission and set its approval outcome',
  'request.rejected.policy': 'A submission was refused by policy before reaching approval',

  // ------------------------------------------------------------ approvals
  'approval.requested': 'An approval request was raised and routed to eligible approvers',
  'approval.granted': 'An approver granted an approval',
  'approval.rejected': 'An approver rejected a request',
  'approval.expired': 'An approval request passed its TTL without decision',
  'approval.cancelled': 'The requester withdrew an approval request',
  'approval.consumed': 'A single-use approval token was consumed by an execution',
  'approval.payload.mismatch':
    'Execution refused because the payload hash did not match the approved hash',
  'approval.rule.created': 'An approval policy rule was created',
  'approval.rule.updated': 'An approval policy rule was changed',
  'approval.rule.deleted': 'An approval policy rule was removed',
  'approval.rule.weakened':
    'An approval policy change reduced the number of approvers, lengthened the TTL, or disabled a rule',

  // ----------------------------------------------------------------- jobs
  'job.enqueued': 'A job was placed on a queue',
  'job.started': 'A worker began executing a job',
  'job.progress': 'Progress checkpoint emitted during execution',
  'job.succeeded': 'A job completed successfully',
  'job.failed': 'A job failed',
  'job.cancelled': 'A job was cancelled',
  'job.timeout': 'A job exceeded its statement or wall-clock timeout',
  'job.throttled': 'A job was deferred by the rate limiter or concurrency governor',
  'job.dead_lettered': 'A job exhausted its retries and moved to the dead-letter queue',

  // ------------------------------------------------------------ execution
  'query.executed': 'A SQL statement was executed against a target',
  'query.blocked': 'A SQL statement was blocked by the safety gate',
  'api.request.executed': 'An HTTP request was issued to an approved endpoint',
  'api.request.blocked': 'An HTTP request was blocked by the endpoint allowlist or SSRF guard',
  'schema.introspected': 'Schema metadata was read from a target',

  // -------------------------------------------------------------- results
  'result.presigned': 'A presigned download URL was issued for a result object',
  'result.downloaded': 'A result object was downloaded (observed via S3 data events)',
  'result.expired': 'A result object was removed by the lifecycle policy',

  // ---------------------------------------------------------------- audit
  'audit.searched': 'The audit ledger was searched',
  'audit.exported': 'A signed evidence pack or entitlement snapshot was exported',
  /**
   * Started and completed are separate events rather than one on success.
   * A bulk read of the ledger that failed halfway through still read most
   * of it, and the attempt is what a reviewer of evidence issuance cares
   * about. One event on completion would leave an aborted export of ten
   * years of data with no trace at all.
   */
  'audit.export.started': 'An evidence pack export began',
  'audit.export.completed': 'An evidence pack export finished and was signed',
  'audit.access.reviewed': 'The entitlement snapshot report was generated for an access review',
  'audit.chain.verified': 'The hash chain was verified end to end',
  'audit.chain.broken': 'Hash chain verification failed',
  'audit.checkpoint.written': 'A signed daily checkpoint was written to the WORM bucket',

  // ------------------------------------------------------- script library
  'script.created': 'A script was saved to the library',
  'script.updated': 'A script\u2019s name, description, tags or dialects changed',
  'script.version.created': 'A new version of a script was saved',
  'script.archived': 'A script was removed from the library',
  'script.publication.requested': 'Publication of a script version was requested',
  'script.publication.granted': 'An approver granted a script publication',
  'script.publication.rejected': 'An approver rejected a script publication',
  'script.publication.cancelled': 'A script publication request was withdrawn by its requester',
  'script.publication.expired': 'A script publication request expired undecided',
  'script.published': 'A script version became visible to a role or to everyone',
  'script.unpublished': 'A script was withdrawn and returned to private',

  // ------------------------------------------------------- administration
  'admin.config.changed': 'A system configuration value was changed',
  'breakglass.activated': 'Emergency elevated access was activated',
  'breakglass.expired': 'Emergency elevated access expired or was revoked',
} as const;

export type AuditEventType = keyof typeof AUDIT_EVENT_TYPES;

export const AUDIT_EVENT_TYPE_KEYS = Object.keys(AUDIT_EVENT_TYPES) as AuditEventType[];

export function isAuditEventType(value: string): value is AuditEventType {
  return Object.prototype.hasOwnProperty.call(AUDIT_EVENT_TYPES, value);
}

/**
 * Events that must raise an operational alert in addition to being recorded.
 * A broken hash chain or a payload mismatch is a control failure, not an
 * ordinary application error, and has to reach a human the same day.
 */
export const ALERTING_EVENT_TYPES: readonly AuditEventType[] = [
  'audit.chain.broken',
  'approval.payload.mismatch',
  /**
   * Policy weakening alerts, control failures do not. Deleting a rule
   * always reduces coverage, so it is unconditionally here; an update only
   * qualifies when it reduces the requirement, which is why the weakening
   * case gets its own type rather than making every edit noisy.
   */
  'approval.rule.weakened',
  'approval.rule.deleted',
  /**
   * Same reasoning as approval-rule weakening. A widened egress range is
   * the change an SSRF review starts from, and a raised row cap on a
   * production target is a data-volume decision somebody should see the
   * same day rather than at the next quarterly review.
   */
  'target.weakened',
  'target.secret.bound',
  'breakglass.activated',
  'role.sod.override',
  'api.request.blocked',
  'query.blocked',
  /**
   * No `script.*` event is here, which is a decision rather than an
   * omission. Publication approval is a *preventive* control, so a granted
   * publication is the expected path working correctly -- and an inbox that
   * pages on the normal case is an inbox people mute, which costs the
   * alerts above their audience.
   *
   * The one script event that would qualify is a hash mismatch on grant,
   * and that raises `approval.payload.mismatch`, already listed, because it
   * means the same thing there as it does here: text moved underneath a
   * decision somebody made about it.
   */
];

export function isAlertingEvent(type: AuditEventType): boolean {
  return ALERTING_EVENT_TYPES.includes(type);
}

/**
 * The JSON property that carries the event type on a log line a CloudWatch
 * metric filter reads.
 *
 * A constant rather than a literal spelled out in each place, because the
 * two places are in different trees: the server writes the line and the CDK
 * declares the filter that matches it. They were written independently as
 * `eventType` and `$.event`, and nothing was wrong on its own face -- the
 * logger logged, the filters synthesised, the alarms existed and sat in OK.
 * They had simply never matched a single line, so every control alarm was
 * decoration for the three weeks before anyone compared the two strings.
 *
 * Anything emitting a line meant to be alarmed on, or any filter meant to
 * catch one, goes through here. The same reasoning as
 * `SQS_MAX_RECEIVE_COUNT`: a number or a name shared across a boundary is
 * one fact, and two copies of one fact are free to disagree.
 */
export const CONTROL_EVENT_LOG_FIELD = 'event';

/** The marker that distinguishes a control-event line from any other log. */
export const CONTROL_EVENT_LOG_MARKER = 'CSA_CONTROL_EVENT';

/**
 * Control events that exist only as log lines and never as ledger rows.
 *
 * One, so far: an append to the ledger that failed. It cannot be recorded
 * as a ledger event for the obvious reason, and it is the most serious
 * thing this system can report -- a privileged action happened and nothing
 * durable remembers it.
 *
 * It gets its own name rather than being folded into `audit.chain.broken`
 * because the cause and the response are different. A broken chain means
 * existing rows disagree with their hashes; a failed write means a row that
 * should exist does not, and the recovery is to find the action in the
 * application log and enter it by hand.
 */
export const CONTROL_LOG_ONLY_EVENTS = ['audit.write.failed'] as const;
export type ControlLogOnlyEvent = (typeof CONTROL_LOG_ONLY_EVENTS)[number];

/** Anything a control metric filter is allowed to match on. */
export type ControlLogEvent = AuditEventType | ControlLogOnlyEvent;

/** Coarse grouping used by the audit search facets in the admin UI. */
export const AUDIT_FACETS = {
  authentication: ['auth.'],
  authorization: ['authz.'],
  identity: ['user.', 'role.'],
  configuration: ['target.', 'admin.', 'breakglass.'],
  execution: ['request.', 'job.', 'query.', 'api.', 'schema.'],
  approval: ['approval.'],
  results: ['result.'],
  ledger: ['audit.'],
  /**
   * Its own facet rather than folded into `configuration`, and not optional:
   * `facetFor` falls through to `execution`, so without an entry here every
   * `script.*` event files itself next to `query.executed` -- which is the
   * one thing saving a script is not. Script publication approvals would
   * also land under `execution` rather than near the approvals they
   * resemble, which is exactly the wrong place for a reviewer to find them.
   */
  library: ['script.'],
} as const satisfies Record<string, readonly string[]>;

export type AuditFacet = keyof typeof AUDIT_FACETS;

export function facetFor(type: AuditEventType): AuditFacet {
  for (const [facet, prefixes] of Object.entries(AUDIT_FACETS)) {
    if (prefixes.some((p) => type.startsWith(p))) return facet as AuditFacet;
  }
  return 'execution';
}
